const DECIMAL_AMOUNT_PATTERN = /^(-?)(\d+)(?:\.(\d+))?$/

interface ScaledDecimal {
  scale: number
  value: bigint
}

const parseDecimalString = (amount: string): ScaledDecimal | null => {
  const match = DECIMAL_AMOUNT_PATTERN.exec(amount.trim())
  if (!match) {
    return null
  }

  const [, sign = "", whole = "0", fraction = ""] = match

  return {
    scale: fraction.length,
    value: (sign === "-" ? -1n : 1n) * BigInt(`${whole}${fraction}`),
  }
}

const formatScaledInteger = (value: bigint, scale: number): string => {
  const negative = value < 0n
  const digits = (negative ? -value : value).toString().padStart(scale + 1, "0")
  const sign = negative ? "-" : ""

  if (scale === 0) {
    return `${sign}${digits}`
  }

  return `${sign}${digits.slice(0, -scale)}.${digits.slice(-scale)}`
}

/** A plain decimal string, or null when the value is blank or not a decimal. */
export const toDecimalAmountString = (
  amount: string | null | undefined
): string | null => {
  const trimmed = amount?.trim() ?? ""
  const parsed = parseDecimalString(trimmed)
  if (!parsed) {
    return null
  }

  return formatScaledInteger(parsed.value, parsed.scale)
}

/**
 * Adds decimal amount strings without binary floating point.
 * `Number("0.1") + Number("0.2")` is not `"0.3"`.
 * Strings that are not plain decimals are skipped. An empty sum is `"0"`.
 */
export const sumDecimalStrings = (amounts: readonly string[]): string => {
  const parsed = amounts.flatMap((amount) => {
    const value = parseDecimalString(amount)
    return value == null ? [] : [value]
  })

  if (parsed.length === 0) {
    return "0"
  }

  const scale = parsed.reduce(
    (maxScale, amount) => Math.max(maxScale, amount.scale),
    0
  )
  const total = parsed.reduce((sum, amount) => {
    const factor = 10n ** BigInt(scale - amount.scale)
    return sum + amount.value * factor
  }, 0n)

  return formatScaledInteger(total, scale)
}
